<template>
  <div
    :class="[
      $style.container,
      {
        [$style.mobile]: isTouchLikeDevice,
        [$style.shadow]: !noAction && !isTouchLikeDevice,
        [$style.sideBySide]: useMobileSideBySide
      }
    ]"
    itemscope
    itemtype="http://schema.org/Product"
    :data-sku="sku"
    @mouseenter="openAction"
    @mouseleave="closeAction"
    @click="handleCardClick"
  >
    <slot />
    <!-- EXCLUSIVE OFFER ICON & LABEL -->
    <div v-if="isExclusive && !hideExclusiveIcon" :class="$style.exclusive">
      <NsIcon
        :title="showExclusiveOfferLabel ? '' : translations.exclusiveOfferTitle"
        :class="$style.exclusiveIcon"
        icon-name="icon-exclusive-offer"
        size="custom"
      />
      <span
        :class="[
          $style.exclusiveLabel,
          { [$style.hideOnMobile]: !showExclusiveOfferLabel }
        ]"
      >
        {{ translations.exclusiveOfferTitle }}
      </span>
    </div>

    <!-- SALE LABEL -->
    <div v-if="isSale && salesLabel" :class="$style.salesLabel">
      {{ salesLabel }}
    </div>

    <!-- FAVORITE ICON -->
    <div
      v-if="!isBaseSku && !hideFavorites"
      :class="$style.favorite"
      @click.stop="handleFavoriteClick"
    >
      <NsIcon
        :title="translations.addToFavorites"
        role="checkbox"
        :aria-checked="isFavorite"
        :class="[$style.favoriteIcon, { [$style.favorited]: isFavorite }]"
        :icon-name="
          isFavorite ? 'icon-favorite-heart-hollow' : 'icon-favorite-heart'
        "
      />
      <NsIcon
        :class="[$style.favoriteIconHover, { [$style.favorited]: isFavorite }]"
        icon-name="icon-favorite-heart-hollow"
      />
    </div>

    <!-- IMAGE -->
    <div :class="$style.imageWrapper">
      <NsImage
        :class="$style.image"
        :src="image.src"
        :alt="image.alt"
        :fallback-src="imageFallbackSrc"
        maintain-ratio="1x1"
        image-fit="contain"
        size-lock="2"
        itemprop="image"
      />
    </div>

    <div
      :class="[
        $style.mainContent,
        { [$style.hasExclusiveOffer]: showExclusiveOfferMessage }
      ]"
    >
      <!-- TITLE -->
      <!-- eslint-disable-next-line -->
      <div :class="$style.title" itemprop="name" v-html="title" />

      <!-- SKU & POINTS -->
      <NsProductSkuPoints
        v-if="showSkuPrices"
        :class="$style.skuPoints"
        :forcesv="forcesv"
        :hide-sku="hideSku || noAction"
        :is-distributor="isDistributor"
        :points="points"
        :sku="sku"
        :translations="translations"
        :hide-before="useMobileSideBySide"
        :use-new-line="useMobileSideBySide"
      />

      <!-- PRICES -->
      <NsProductPrice
        v-if="showPrices"
        :class="$style.price"
        :adr-only="showAdrOnly"
        :adr-price="adrPrice"
        :adr-price-label="translations.adrPriceLabel"
        :adr-price-type="adrPriceType"
        :is-base-sku="isBaseSku"
        :min-price="minPrice"
        :max-price="maxPrice"
        :original-price="originalPrice"
        :original-price-label="originalPriceLabel"
        :original-price-type="originalPriceType"
        :price="price"
        :price-label="priceLabel"
        :price-type="priceType"
        :show-labels="showPriceLabels"
        :show-original-crossed-out="showOriginalCrossedOut"
        :show-adr-price="showAdrPrice"
        :hide-adr-override="!showAdrOnly"
      />

      <!-- VARIANT SELECTOR -->
      <NsProductVariantSelector
        v-if="showVariantSelector && !noAction"
        :class="$style.variantSelector"
        :placeholder="translations.variantDropdownPlaceholder"
        :selected-sku="isBaseSku ? '' : sku"
        :disabled="disabled"
        :variant-skus="variantSkus"
        :variants="variants"
        :size="useMobileSideBySide ? 'small' : undefined"
        @select="handleSelectVariant"
      />
    </div>

    <!-- ACTION -->
    <div
      :class="[
        $style.action,
        {
          [$style.static]: isTouchLikeDevice,
          [$style.shadowAction]: !isTouchLikeDevice
        }
      ]"
    >
      <NsAnimateHeight
        v-if="!noAction"
        :open="actionOpened || isTouchLikeDevice || showAction"
      >
        <NsProductCardAction
          :disable-add-to-adr="disableAddToAdr"
          :disable-add-to-cart="disableAddToCart"
          :disable-quantity-selector="disableQuantitySelector"
          :exclusive-offer-message="exclusiveOfferMessage"
          :max-quantity="maxQuantity"
          :mobile-side-by-side="useMobileSideBySide"
          :quantity="quantity"
          :show-exclusive-offer-message="showExclusiveOfferMessage"
          :status-message="statusMessage"
          :status-message-key="statusMessageKey"
          :translations="translations"
          @quantity-select="handleQuantitySelect"
          @add-to-cart="handleAddToCart"
          @add-to-adr="handleAddToAdr"
          @go-to-details="handleCardClick"
        />
      </NsAnimateHeight>
    </div>
  </div>
</template>

<script>
import { NsIcon, NsAnimateHeight } from "@nuskin/design-components";
import NsImage from "./NsImage.vue";
import NsProductCardAction from "./NsProductCardAction.vue";
import NsProductPrice from "./NsProductPrice.vue";
import NsProductSkuPoints from "./NsProductSkuPoints.vue";
import NsProductVariantSelector from "./NsProductVariantSelector.vue";

/**
 * NsProductCardBase
 *
 * How to use: You shouldn't use this component. Use NsProductCard instead. It
 * includes the Product mixin. This component is simply all the html and styles.
 *
 * @event variant-select  When a variant selection occurs, this event emits a
 * function with params: (value, index)
 * @event quantity-select  When a quantity selection occurs, this event emits a
 * function with params: (value, index)
 * @event add-to-cart  When clicking on "Add to Cart", this event emits a
 * function with params: ()
 * @event add-to-adr  When clicking on "Add to Adr", this event emits a function
 * with params: ()
 * @event card-click  When clicking on the ProductCard, this event emits a
 * function with params: (product). If listener is NOT assigned, clicking the
 * card will go to the product detail page
 * @event favorite-click  When clicking on the favorite icon, this event emits a
 * function with params: ()
 */

export default {
  name: "NsProductCardBase",
  components: {
    NsIcon,
    NsImage,
    NsProductPrice,
    NsProductCardAction,
    NsAnimateHeight,
    NsProductSkuPoints,
    NsProductVariantSelector
  },
  props: {
    /**
     * ADR price value
     */
    adrPrice: {
      type: String,
      default: ""
    },
    adrPriceType: {
      type: String,
      default: ""
    },
    disableAddToAdr: Boolean,
    disableAddToCart: Boolean,
    disableQuantitySelector: Boolean,
    exclusiveOfferMessage: {
      type: String,
      default: ""
    },
    forcesv: Boolean,
    hideExclusiveIcon: Boolean,
    hideExclusiveTextOnMobile: Boolean,
    hideFavorites: Boolean,
    hideSku: Boolean,
    // TODO: src/alt/fallbackSrc props to image prop
    imageFallbackSrc: {
      type: String,
      default: ""
    },
    image: {
      type: Object,
      default() {
        return {};
      }
    },
    imageSet: {
      type: Array,
      default() {
        return [];
      }
    },
    isBaseSku: Boolean,
    isDistributor: Boolean,
    isExclusive: Boolean,
    isFavorite: Boolean,
    isLoggedIn: Boolean,
    isLoyaltyEnabled: Boolean,
    isSale: Boolean,
    loyaltyStatusAndLinks: {
      type: Object,
      default() {
        return {};
      }
    },
    marketAttributes: {
      type: Object,
      default() {
        return {};
      }
    },
    maxQuantity: {
      type: Number,
      default: null
    },
    maxPrice: {
      type: String,
      default: ""
    },
    minPrice: {
      type: String,
      default: ""
    },
    /**
     * Determines if the product card should have the small-width styling to support side-by-side in mobile
     */
    mobileSideBySide: Boolean,
    noAction: Boolean,
    originalPrice: {
      type: String,
      default: ""
    },
    originalPriceLabel: {
      type: String,
      default: ""
    },
    originalPriceType: {
      type: String,
      default: ""
    },
    points: {
      type: String,
      default: null
    },
    price: {
      type: Number,
      default: null
    },
    priceLabel: {
      type: String,
      default: ""
    },
    priceType: {
      type: String,
      default: ""
    },
    quantity: {
      type: String,
      default: "1"
    },
    salesLabel: {
      type: String,
      default: ""
    },
    /**
     * This prop is only for storybook for simple dev use
     */
    showAction: Boolean,
    showAdrPrice: Boolean,
    showAdrOnly: Boolean,
    showExclusiveOfferMessage: Boolean,
    showOriginalCrossedOut: Boolean,
    showPriceLabels: Boolean,
    showVariantSelector: Boolean,
    sku: {
      type: String,
      default: ""
    },
    /**
     * Warning message, if product is out of stock or whatever
     */
    statusMessage: {
      type: String,
      default: ""
    },
    /**
     * Warning message key, for qa testing automation
     */
    statusMessageKey: {
      type: String,
      default: ""
    },
    title: {
      type: String,
      default: ""
    },
    translations: {
      type: Object,
      default() {
        return {};
      }
    },
    userIsQualified: Boolean,
    userLoyalty: {
      type: Object,
      default() {
        return {};
      }
    },
    variantSkus: {
      type: Array,
      default() {
        return [];
      }
    },
    variants: {
      type: Array,
      default() {
        return [];
      }
    }
  },
  data() {
    return {
      actionOpened: false,
      initSku: null,
      product: null,
      variantIndex: null
    };
  },
  computed: {
    isTouchLikeDevice() {
      return (
        this.isMobileDevice() || this.$mq === "mobile" || this.$mq === "phablet"
      );
    },
    showPrices() {
      return (
        ((this.price && this.price !== "0") ||
          (this.minPrice && this.minPrice !== "0")) &&
        !(this.isExclusive && !this.userIsQualified && !this.noAction)
      );
    },
    showSkuPrices() {
      return !this.isBaseSku && (!this.noAction || this.forcesv);
    },
    showExclusiveOfferLabel() {
      return !(this.hideExclusiveTextOnMobile || this.useMobileSideBySide);
    },
    useMobileSideBySide() {
      return (
        (this.$mq === "mobile" || this.$mq === "phablet") &&
        this.mobileSideBySide
      );
    }
  },
  methods: {
    closeAction() {
      this.actionOpened = false;
    },
    handleQuantitySelect(value, index) {
      /**
       * Fired when a new quantity is selected: function(value: string|number,
       * index: number) => void
       *
       * @type {select}
       * @property {(string|number)} value data-value attribute of the selected
       * item, or index
       * @property {number} index index of the selected item
       */
      this.$emit("quantity-select", value, index);
    },
    handleAddToCart(event) {
      /**
       * Fired when "add to cart" button is clicked: function(event) => void
       *
       * @type {click}
       * @property {event} event the click event
       */
      this.$emit("add-to-cart", event);
    },
    handleAddToAdr(event) {
      /**
       * Fired when "add to cart" button is clicked: function(event) => void
       *
       * @type {click}
       * @property {event} event the click event
       */
      this.$emit("add-to-adr", event);
    },
    handleSelectVariant(selected, index) {
      /**
       * Fired when a new variant is selected: function(value: string|number,
       * index: number) => void
       *
       * @type {select}
       * @property {(string|number)} value data-value attribute of the selected
       * item, or index
       * @property {number} index index of the selected item
       */
      this.$emit("variant-select", selected, index);
    },
    /**
     * Checks if device is mobile (will use modal if so)
     */
    isMobileDevice() {
      return (
        typeof window.orientation !== "undefined" ||
        navigator.userAgent.includes("IEMobile")
      );
    },
    handleCardClick() {
      this.$emit("card-click");
    },
    handleFavoriteClick() {
      this.$emit("favorite-click");
    },
    openAction() {
      this.actionOpened = true;
    }
  }
};
</script>

<style lang="scss" module>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/other";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";

$z-primary: map.get(other.$otherUtils, z-primary);

* {
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
}

.container {
  position: relative;
  margin-left: 0;
  margin-right: 0;
  min-width: 230px;
  color: map.get(colors.$themeColors, color-interactive-7);
  background-color: white;
  @include global.p-small;
  text-align: center;
  transition: box-shadow 0.3s ease-in-out;
  cursor: pointer;

  &.shadow:hover {
    box-shadow: rgba(0, 0, 0, 0.15) 0 8px 8px 0;
  }
}

// TOP ICONS AND LABELS

.exclusive {
  position: absolute;
  top: 11px;
  left: 10px;
  display: flex;
  align-items: center;
  color: map.get(colors.$themeColors, color-interactive-7);
  font-size: 13px;
  font-weight: 300;
  line-height: 13px;
  z-index: $z-primary;
}

.exclusiveIcon {
  height: 22px;
  width: 22px;
}

.exclusiveLabel {
  margin-left: 8px;
}

.salesLabel {
  position: absolute;
  top: 10px;
  left: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 5px 0;
  height: 20px;
  color: white;
  background-color: map.get(colors.$themeColors, color-interactive-7);
  @include global.subtitle-small-bold-caps;
  font-weight: map.get(typographyVariables.$typography, "font-weight-semibold");
  letter-spacing: 1px;
  z-index: $z-primary;
}

.salesLabel::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 100%;
  border-top: 10px solid map.get(colors.$themeColors, color-interactive-7);
  border-bottom: 10px solid map.get(colors.$themeColors, color-interactive-7);
  border-right: 6px solid transparent;
  width: 0;
  font-size: 0;
  line-height: 0;
}

.favorite {
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 10px;
  z-index: $z-primary;
}

.container:not(.mobile) {
  .favorite:hover svg.favoriteIconHover {
    opacity: 0.3;

    &.favorited {
      opacity: 0.5;
    }
  }
}

svg.favoriteIcon {
  fill: white;
  transition: map.get(other.$otherUtils, global-transition);

  &.favorited {
    fill: map.get(colors.$baseColors, mermaid);
    color: map.get(colors.$baseColors, mermaid);
  }
}

svg.favoriteIconHover {
  position: absolute;
  top: 10px;
  right: 10px;
  opacity: 0;
  fill: map.get(colors.$baseColors, mermaid);
  color: map.get(colors.$baseColors, mermaid);
  z-index: $z-primary;

  &.favorited {
    fill: map.get(colors.$baseColors, ariel);
    color: map.get(colors.$baseColors, ariel);
  }
}

// IMAGE

.imageWrapper {
  padding: 30px 20px 0px;
  width: 100%;
}

.image {
  display: block;
  margin: 0 auto;
  width: 100%;
  max-width: 165px;
}

// MAIN CONTENT

.mainContent {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-gap: 10px;
  padding: 10px 20px 20px;
  width: 100%;
}
.title {
  padding-bottom: 2px;
  max-height: 60px;
  @include global.title-small;
  overflow: hidden;
}

.title sup {
  font-size: inherit;
  line-height: 1;
  vertical-align: top;
}

.price {
  justify-content: center;
}

.variantSelector {
  margin-top: 10px;
}

// LOWER CONTENT (ACTION)

.action {
  user-select: none;
  z-index: $z-primary + 1;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: -10px;
  width: 100%;

  &.static {
    position: static;
  }

  &.shadowAction {
    box-shadow: rgba(0, 0, 0, 0.15) 0 8px 8px 0;
  }
}

@media (max-width: 767px) {
  .container.sideBySide {
    min-width: 160px;
    max-width: 200px;
    font-size: 16px;
    padding: 0;

    .imageWrapper {
      padding: 12px 12px 10px;
    }

    .mainContent {
      position: relative;
      grid-gap: 6px;
      padding: 12px;
      text-align: left;
      background: map.get(colors.$themeColors, color-background-1);

      &.hasExclusiveOffer {
        background: #f9f0f9;
      }
    }

    .title {
      font-size: 16px;
      font-weight: 600;
      line-height: 18px;
      max-height: unset;
    }

    .skuPoints {
      justify-content: flex-start;
    }

    .price {
      justify-content: flex-start;
    }

    .variantSelector {
      font-size: 16px;
    }

    .action {
      margin-top: 0;
    }
  }

  .hideOnMobile {
    display: none;
  }
}

@media (min-width: 1024px) {
  .container {
    min-width: 280px;
  }
}
</style>
